/**
 * Sitter Onboarding Guards
 *
 * These guards handle navigation logic specific to the sitter onboarding flow.
 * They manage care type routing and redirect completed users away from onboarding.
 *
 * Guards:
 * - sitterOnboardingBeforeEach: Global guard that handles external routes and header restoration
 * - handleExternalRoutes: Handles external routes (legacy Sittercity routes) by using full page navigation
 * - redirectLoggedInSittersFromAccountCreation: Redirects logged-in sitters (complete or incomplete) from account creation page
 * - checkSitterOnboardingRedirect: Redirects completed users away from onboarding pages
 * - careTypeBeforeEnter: Handles care-type route logic with additional profile bypass
 * - profileStepsBeforeEnter: Ensures valid care type before accessing profile steps
 * - setCareTypeGuard: Sets a specific care type in the store before entering route
 */
import { Route, NavigationGuardNext } from 'vue-router';
import camelCase from 'lodash/camelCase';
import { setupApollo } from '@/lib/apollo_setup';
import {
  redirectIfOnboarded,
  sitterOnboardingState,
  routeForOnboardingState,
  careTypeToPathMap,
  externalRoutes,
  isChildCareType,
} from '@/lib/sitterOnboarding/sitterOnboardingState';
import { readDomainCookie } from '@/lib/domain_cookie';
import { checkJwtRoles } from './helpers';
import store from '../../store';
import router from '../index';

/**
 * Handles external routes (legacy Sittercity routes) by using full page navigation.
 * This prevents Vue Router from trying to match these routes and showing "Page not found".
 * Only checks routes that start with /sitter/ to avoid unnecessary checks on all routes.
 * Returns true if an external route was handled (navigation aborted), false otherwise.
 */
const handleExternalRoutes = (_to: Route, _from: Route, next: NavigationGuardNext): boolean => {
  if (_to.path.startsWith('/sitter/')) {
    if ((externalRoutes as readonly string[]).includes(_to.path)) {
      window.location.assign(_to.path);
      next(false);
      return true;
    }
  }

  return false;
};

/**
 * Global beforeEach guard for sitter onboarding.
 * Handles external routes (legacy Sittercity routes).
 */
export const sitterOnboardingBeforeEach = (to: Route, from: Route, next: NavigationGuardNext): void => {
  // Handle external routes (legacy Sittercity routes) first
  // If it's an external route, handleExternalRoutes will call next(false) and return true
  if (handleExternalRoutes(to, from, next)) {
    return;
  }

  next();
};

/**
 * Extracts a single value from route query parameters, handling both array and single value cases.
 */
function getQueryParam(query: Record<string, string | (string | null)[] | null> | undefined, key: string): string | null {
  if (!query || !query[key]) return null;
  const value = query[key];
  return Array.isArray(value) ? value[0] : value;
}

/**
 * Gets and syncs the additional profile status from query params and cookie.
 */
function getAdditionalProfileFromQuery(query?: Record<string, string | (string | null)[] | null>): boolean {
  const queryValue = getQueryParam(query, 'additionalProfile');
  const cookieValue = readDomainCookie('additional_profile') === 'true';
  return queryValue === 'true' || cookieValue;
}

/**
 * Reads care type from route query parameters or Vuex store (via sessionStorage).
 * If a careType is in the route query, it's also dispatched to the store.
 * Checks for additional profile creation via route query parameter (preferred) or domain cookie (fallback).
 * @param routeQuery - Route query object from Vue Router
 */
export function setCareTypeFromUrlOrStore(routeQuery: Record<string, string | (string | null)[] | null>): {
  careType: string | null;
  isAdditionalProfile: boolean;
} {
  try {
    const careTypeParam = getQueryParam(routeQuery, 'careType');

    const vuexData = JSON.parse(sessionStorage.getItem('vuex') || '{}');
    const storeCareType = vuexData?.sitterOnboarding?.careType;

    const careType = careTypeParam ? camelCase(careTypeParam) : storeCareType;

    if (careTypeParam && store && store.dispatch) {
      store.dispatch('setCareType', careType);
    }

    const isAdditionalProfile = getAdditionalProfileFromQuery(routeQuery);

    return { careType, isAdditionalProfile };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error setting care type:', error);
    return { careType: null, isAdditionalProfile: false };
  }
}

/**
 * Redirects logged-in sitters (complete or incomplete) away from the account creation page.
 * Incomplete sitters are redirected to their current onboarding step.
 * Completed sitters are redirected to the dashboard.
 * Allows new users (not logged in) to access the account creation page.
 */
export const redirectLoggedInSittersFromAccountCreation = async (_to: Route, _from: Route, next: NavigationGuardNext) => {
  let account = store.getters['account/getAccount'];
  let isSitter = false;

  // Check store account first
  if (account?.isSitter()) {
    isSitter = true;
  } else if (!account || typeof account.isSitter !== 'function') {
    // If store account is not available, check JWT data directly
    const jwtCheck = checkJwtRoles();
    if (jwtCheck.isSitter) {
      isSitter = true;
      // Fetch account data if missing before proceeding with redirect
      if (!account) {
        try {
          await store.dispatch('account/fetchAccountData');
          account = store.getters['account/getAccount'];
        } catch (error) {
          console.error('Error fetching account data in redirectLoggedInSittersFromAccountCreation:', error);
        }
      }
    }
  }

  if (isSitter) {
    try {
      const apolloProvider = setupApollo();
      const onboardingState = await sitterOnboardingState(apolloProvider);

      if (onboardingState !== 'COMPLETE') {
        // Always redirect from account creation page, regardless of isAdditionalProfile
        const targetRoute = routeForOnboardingState(onboardingState);
        if (_to.path !== targetRoute) {
          next(targetRoute);
          return;
        }
      } else {
        next('/account/providers/dashboard');
        return;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in redirectLoggedInSittersFromAccountCreation:', error);
    }
  }

  next();
};

/**
 * Redirects users who have already completed onboarding away from onboarding pages.
 * Used on onboarding routes to prevent completed users from re-entering the flow.
 */
export const checkSitterOnboardingRedirect = async (_to: Route, _from: Route, next: NavigationGuardNext) => {
  const account = store.getters['account/getAccount'];

  if (account?.isSitter()) {
    try {
      const apolloProvider = setupApollo();
      const targetRoute = await redirectIfOnboarded(apolloProvider, account, router, _to.path, _to.query);
      if (targetRoute) {
        next(targetRoute);
        return;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in checkSitterOnboardingRedirect:', error);
    }
  }

  next();
};

/**
 * Before-enter guard for the care-type route.
 * If user is adding an additional profile and has a care type, bypasses care type selection.
 */
export const careTypeBeforeEnter = (_to: Route, _from: Route, next: NavigationGuardNext) => {
  const { careType, isAdditionalProfile } = setCareTypeFromUrlOrStore(_to.query);

  if (isAdditionalProfile && careType) {
    if (isChildCareType(careType)) {
      next('/sitter/signup/profile/steps');
      return;
    }
    const normalizedCareType = camelCase(careType);
    const profilePath = careTypeToPathMap[normalizedCareType] || '/sitter/signup/care-type';
    next(profilePath);
    return;
  }

  next();
};

/**
 * Before-enter guard for the profile steps route (child care only).
 * Redirects to care-type selection if no valid child care type is set.
 */
export const profileStepsBeforeEnter = (_to: Route, _from: Route, next: NavigationGuardNext) => {
  const { careType } = setCareTypeFromUrlOrStore(_to.query);

  if (!careType || !isChildCareType(careType)) {
    next('/sitter/signup/care-type');
    return;
  }

  next();
};

/**
 * Creates a before-enter guard that sets a specific care type in the store.
 * Used for housekeeping, pet-care, and companion-care routes.
 * Prefers careType from query parameter if present, otherwise uses the provided default.
 */
export const setCareTypeGuard = (defaultCareType: string) => {
  return (_to: Route, _from: Route, next: NavigationGuardNext) => {
    // Check if careType is provided in query parameters (for additional profiles)
    const queryCareType = getQueryParam(_to.query, 'careType');
    const careTypeToSet = queryCareType ? camelCase(queryCareType) : defaultCareType;
    store.dispatch('setCareType', careTypeToSet);
    next();
  };
};
